import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItemText,
  Drawer,
  ListItemButton,
  ListItemIcon,
  Menu,
  Divider,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import NotificationImportantOutlinedIcon from "@mui/icons-material/NotificationImportantOutlined";
import {
  ROUTE_APPLICATION,
  ROUTE_DASHBOARD,
  ROUTE_EVENTLOG,
  ROUTE_LOGIN,
  ROUTE_MEMBER,
  ROUTE_PROJECT,
  ROUTE_THING,
  ROUTE_THING_INFOMAIN,
  USER_EMAIL,
  USER_NAME,
} from "../constants";
import IconButtonBox from "../components/IconButtonBox";
import "../style/MainLayout.css";
import {
  getLocalStorage,
  logout,
} from "../utils/functions";
import useThingStore from "../stores/ThingStore";
import DialogBox from "../components/DialogBox";
import useGlobalStore from "../stores/GlobalStore";
import { ROUTE_PROFILE } from "../constants/index";
import useAuthCheck from "../utils/useAuthCheck";

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { showErrMsg, errMsg, setShowErrMsg, setErrMsg, showTokenExpired, setShowTokenExpired } = useGlobalStore();

  const { tabIndex, setTabIndex, showTab, setShowTab } = useThingStore();

  useAuthCheck();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTE_LOGIN);
  };

  const goToProfile = () => {
    handleMenuClose();
    navigate(ROUTE_PROFILE);
  };

  const doLogout = () => {
    setShowTokenExpired(false);
    handleLogout();
  }

  useEffect(() => {
    if (location.pathname === ROUTE_THING_INFOMAIN) {
      setShowTab(true);
    } else {
      setShowTab(false);
    }
  }, [location.pathname, setShowTab]);

  return (
    <Box className="main-layout">
      <Drawer variant="permanent" className="drawer">
        <Typography variant="h5" className="drawer-title">
          <MenuIcon />
          IIoT Platform
        </Typography>
        <Divider className="divider" />
        <List>
          {[
            {
              text: "Dashboard",
              icon: <HomeOutlinedIcon />,
              path: ROUTE_DASHBOARD,
            },
            {
              text: "Thing",
              icon: <StarBorderOutlinedIcon />,
              path: ROUTE_THING,
            },
            // { text: 'Command', icon: <TuneIcon />, path: '/' },
            // { text: 'Flow', icon: <AccountTreeOutlinedIcon />, path: '/' },
            {
              text: "Project",
              icon: <PrecisionManufacturingOutlinedIcon />,
              path: ROUTE_PROJECT,
            },
            {
              text: "Application",
              icon: <AppsIcon />,
              path: ROUTE_APPLICATION,
            },
            {
              text: "Member",
              icon: <PersonOutlineOutlinedIcon />,
              path: ROUTE_MEMBER,
            },
            {
              text: "Event Log",
              icon: <NotificationImportantOutlinedIcon />,
              path: ROUTE_EVENTLOG,
            },
            // { text: 'IIoT Galaxy', icon: <ShoppingBagOutlinedIcon />, path: '/' },
          ].map((item, index) => (
            <ListItemButton component={Link} key={index} to={item.path}>
              <ListItemIcon className="list-item-icon">
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box flexGrow={1}>
        <AppBar className="app-bar">
          <Toolbar className="toolbar">
            {showTab && (
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                textColor="inherit"
                className="custom-tabs"
              >
                <Tab label="Information" />
                <Tab label="Configuration" />
                <Tab label="Data" />
              </Tabs>
            )}

            <Box />

            <Box className="toolbar flex-box">
              <Typography>Hello, {getLocalStorage(USER_NAME)}</Typography>
              <IconButtonBox
                icon={<ExpandMoreIcon />}
                onClick={handleMenuOpen}
              />

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <Box className="menu-box">
                  <Typography className="toolbar-text">ORing</Typography>
                  <Typography>{getLocalStorage(USER_EMAIL)}</Typography>
                </Box>
                <Divider />
                <MenuItem onClick={goToProfile}>My account</MenuItem>
                {/* <MenuItem onClick={handleMenuClose}>Billing</MenuItem> */}
                <MenuItem onClick={handleLogout}>Log out</MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        <Box className="content">
          <Outlet />
        </Box>
        <DialogBox
          open={showErrMsg}
          title="Error"
          dialogContent={<>{errMsg}</>}
          showCancel={false}
          onCancel={() => { }}
          onConfirm={() => {
            setErrMsg("");
            setShowErrMsg(false);
          }}
          confirmLabel="OK"
          isError={true}
        />
      </Box>
      <DialogBox
        open={showTokenExpired}
        title='Token expired'
        dialogContent={
          <>
            Your token expired. Please log in again
          </>
        }
        showCancel={false}
        onCancel={() => { }}
        onConfirm={doLogout}
        isError={false}
        confirmLabel='OK'
      />
    </Box>
  );
};

export default MainLayout;
